export interface Category {
  id: string;
  name: string;
  description: string;
  courseCount: number;
  isExpanded?: boolean;
  subcategories?: Category[];
}

export interface CatalogCourse {
  id: string;
  title: string;
  category: string;
  rating: number;
  duration: string;
  level: string;
}

export interface FeaturedCourse {
  id: string;
  title: string;
  category: string;
  rating: number;
  enrolled: number;
  isActive: boolean;
}

export interface RecommendationRule {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  description: string;
  tags: string[];
}

export const getCategories = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Category[] = [
      {
        id: '1',
        name: 'Leadership & Management',
        description: 'Develop leadership and people management skills',
        courseCount: 45,
        isExpanded: true,
        subcategories: [
          {
            id: '1-1',
            name: 'Executive Leadership',
            description: 'C-level leadership development',
            courseCount: 12,
          },
          {
            id: '1-2',
            name: 'Team Management',
            description: 'Building and managing high-performance teams',
            courseCount: 18,
          },
          {
            id: '1-3',
            name: 'Strategic Planning',
            description: 'Long-term planning and strategy',
            courseCount: 15,
          },
        ],
      },
      {
        id: '2',
        name: 'Technical Skills',
        description: 'Technical and IT competencies',
        courseCount: 78,
        isExpanded: false,
      },
      {
        id: '3',
        name: 'Soft Skills',
        description: 'Communication and interpersonal skills',
        courseCount: 56,
        isExpanded: false,
      },
      {
        id: '4',
        name: 'Compliance & Safety',
        description: 'Regulatory and safety training',
        courseCount: 23,
        isExpanded: false,
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Category[]>(ENDPOINTS.CATEGORIES);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch categories' }
    };
  }
};

export const getCatalogCourses = async (): Promise<{
  success: boolean;
  data?: CatalogCourse[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: CatalogCourse[] = [
      {
        id: '1',
        title: 'Advanced Leadership Development',
        category: 'Leadership & Management',
        rating: 4.8,
        duration: '8 weeks',
        level: 'Advanced',
      },
      {
        id: '2',
        title: 'Data Analytics Fundamentals',
        category: 'Technical Skills',
        rating: 4.6,
        duration: '6 weeks',
        level: 'Beginner',
      },
      {
        id: '3',
        title: 'Project Management Professional',
        category: 'Leadership & Management',
        rating: 4.9,
        duration: '10 weeks',
        level: 'Intermediate',
      },
      {
        id: '4',
        title: 'Effective Communication Skills',
        category: 'Soft Skills',
        rating: 4.7,
        duration: '4 weeks',
        level: 'Beginner',
      },
      {
        id: '5',
        title: 'Python for Data Science',
        category: 'Technical Skills',
        rating: 4.5,
        duration: '7 weeks',
        level: 'Intermediate',
      },
      {
        id: '6',
        title: 'Strategic Planning Masterclass',
        category: 'Leadership & Management',
        rating: 4.8,
        duration: '5 weeks',
        level: 'Advanced',
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 600));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<CatalogCourse[]>(ENDPOINTS.CATALOG_COURSES);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch catalog courses' }
    };
  }
};

export const getFeaturedCourses = async (): Promise<{
  success: boolean;
  data?: FeaturedCourse[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: FeaturedCourse[] = [
      {
        id: '1',
        title: 'Data Analytics Fundamentals',
        category: 'Technical',
        rating: 4.6,
        enrolled: 423,
        isActive: true,
      },
      {
        id: '2',
        title: 'Effective Communication Skills',
        category: 'Soft Skills',
        rating: 4.7,
        enrolled: 512,
        isActive: true,
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 400));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<FeaturedCourse[]>(ENDPOINTS.FEATURED_COURSES);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch featured courses' }
    };
  }
};

export const getRecommendationRules = async (): Promise<{
  success: boolean;
  data?: RecommendationRule[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: RecommendationRule[] = [
      {
        id: '1',
        name: 'New Joiners Onboarding',
        status: 'Active',
        description: 'Show to employees with tenure < 30 days',
        tags: ['Onboarding 101', 'Company Culture', 'Tools & Systems'],
      },
      {
        id: '2',
        name: 'Leadership Track',
        status: 'Active',
        description: 'Show to employees in management roles',
        tags: ['Leadership Essentials', 'Team Building', 'Performance Management'],
      },
      {
        id: '3',
        name: 'Technical Upskilling',
        status: 'Inactive',
        description: 'Show to engineering department',
        tags: ['Advanced Python', 'System Design', 'Cloud Architecture'],
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 450));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<RecommendationRule[]>(ENDPOINTS.RECOMMENDATION_RULES);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch recommendation rules' }
    };
  }
};
